import { useCallback, useId, useMemo, useRef, useState } from 'react';
import type { Id } from '@contracts/common';
import type { FileTarget, UploadCreatedDTO, UploadDTO } from '@contracts/tasks';
import { Paperclip, RotateCcw, X } from 'lucide-react';
import { api, ApiClientError } from '../../api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FIELD_ERROR, FIELD_HINT } from './RecipientField';

/**
 * Files chosen for something that may not exist yet. The bytes never travel through our API: the server
 * hands out a short-lived POST policy, the browser sends the file straight to storage, and then asks the
 * server to go and look at what actually arrived. Only that last answer counts as verified, so a save can
 * carry a file the server has already confirmed rather than a promise from this tab.
 *
 * Everything here lives in tab memory on purpose. These drafts are confidential, and a half-written
 * assignment left in `localStorage` would outlive both the tab and the person's intent to send it.
 */

export type PickedFileStatus = 'queued' | 'uploading' | 'verifying' | 'verified' | 'failed';

export type PickedFile = {
  localId: string;
  uploadId?: Id;
  status: PickedFileStatus;
  error?: string;
  file: File;
};

export const STATUS_LABEL: Record<PickedFileStatus, string> = {
  queued: 'Đang chờ', uploading: 'Đang tải lên', verifying: 'Đang kiểm tra',
  verified: 'Sẵn sàng', failed: 'Không tải được',
};

const STATUS_VARIANT: Record<PickedFileStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  queued: 'neutral', uploading: 'info', verifying: 'info', verified: 'success', failed: 'danger',
};

/** A dashed zone a file can be dropped on, holding the "Đính kèm tệp" button (T5, 2026-09-25). */
const DROP_ZONE = cn(
  'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed border-input bg-white p-1.5',
  'transition-colors data-[dragging]:border-[hsl(var(--h-primary))] data-[dragging]:bg-[hsl(var(--h-primary)/0.06)]',
  'aria-disabled:opacity-50',
);
/** A label dressed as the outline button; the hidden input inside it lends it the keyboard focus. */
const ATTACH_BUTTON = cn(
  'inline-flex h-11 pointer-fine:h-9 cursor-pointer items-center gap-2 rounded-md border border-input px-3 text-sm font-medium',
  'bg-[hsl(var(--h-muted))] text-[hsl(var(--h-card-foreground))] hover:border-[hsl(var(--h-primary)/0.5)]',
  'has-focus-visible:ring-2 has-focus-visible:ring-inset has-focus-visible:ring-ring',
  'data-[disabled]:cursor-not-allowed',
);

/**
 * What each refusal means to the person holding the file. These are the API's real codes, not a guess:
 * a wrong-sized file, a transfer that never completed, a session already used or expired, and a storage
 * bucket the operators have not configured — four different actions, so four different sentences.
 */
export function uploadFailureMessage(error: unknown): string {
  if (!(error instanceof ApiClientError)) return 'Không tải được tệp. Hãy thử lại tệp này.';
  switch (error.code) {
    case 'FILE_SIZE': return 'Tệp vượt quá dung lượng cho phép hoặc rỗng.';
    case 'UPLOAD_INCOMPLETE': return 'Tệp chưa được tải lên đầy đủ. Hãy tải lại tệp này.';
    case 'UPLOAD_STATE': return 'Lượt tải tệp này đã kết thúc hoặc hết hạn. Hãy chọn lại tệp.';
    case 'STORAGE_UNVERSIONED': return 'Kho lưu trữ đang không sẵn sàng. Hãy báo quản trị viên rồi thử lại.';
    case 'VERSION_CONFLICT': return 'Tệp vừa thay đổi ở nơi khác. Hãy tải lại trang rồi thử lại tệp.';
    case 'REQUEST_CONFLICT': return 'Lượt tải này đã được dùng cho tệp khác. Hãy chọn lại tệp.';
    case 'NETWORK': return 'Không gửi được tệp lên kho lưu trữ. Kiểm tra mạng rồi thử lại tệp.';
    case 'FORBIDDEN': return 'Bạn không có quyền đính kèm tệp vào mục này.';
    default: return error.message || 'Không tải được tệp. Hãy thử lại tệp này.';
  }
}

/**
 * The type a session claims. S5 D83: a `.csv` name sent for ingestion is `text/csv` whatever the browser reports (Excel
 * on Windows says `application/vnd.ms-excel`, some browsers nothing); every other target keeps the browser's word.
 */
export function uploadTypeOf(target: FileTarget, file: { name: string; type: string }): string {
  if (target.kind === 'ingest' && file.name.toLowerCase().endsWith('.csv')) return 'text/csv';
  return file.type || 'application/octet-stream';
}

export type FileQueue = {
  files: PickedFile[];
  /** Queues the files and returns their local ids, so a caller can follow the files it picked together. */
  add: (chosen: readonly File[]) => string[];
  remove: (localId: string) => void;
  retry: (localId: string) => void;
  /** The only thing a save may send: sessions the server itself called verified. */
  verifiedIds: Id[];
  /** A file is still moving — saving now would leave it behind. */
  busy: boolean;
  /** A file failed and is still listed; the person removes it or retries it before saving. */
  blocked: boolean;
  /** Called after a save that consumed these sessions, so the next save does not attach them twice. */
  clear: () => void;
  /** Takes out only these sessions once a save consumed them; files picked meanwhile stay for the next save. */
  drop: (uploadIds: readonly Id[]) => void;
};

export function useFileQueue(target: FileTarget): FileQueue {
  const [files, setFiles] = useState<PickedFile[]>([]);
  const nextLocalId = useRef(0);
  // The target can gain an id (a comment draft becoming a comment) while a transfer is in flight.
  const current = useRef(target);
  current.current = target;

  const patch = useCallback((localId: string, changes: Partial<PickedFile>) => {
    setFiles((list) => list.map((entry) => (entry.localId === localId ? { ...entry, ...changes } : entry)));
  }, []);

  const run = useCallback(async (localId: string, file: File) => {
    patch(localId, { status: 'uploading', error: undefined });
    try {
      const created = await api<UploadCreatedDTO>('/api/uploads', {
        method: 'POST',
        body: {
          target: current.current, filename: file.name,
          type: uploadTypeOf(current.current, file), size: file.size,
        },
      });
      patch(localId, { uploadId: created.session.id });

      if (created.upload) {
        const form = new FormData();
        for (const [name, value] of Object.entries(created.upload.fields)) form.append(name, value);
        form.append('file', file);
        let response: Response;
        try {
          // Storage is a foreign origin with its own policy: no cookies, no envelope, no retry.
          response = await fetch(created.upload.url, { method: 'POST', body: form });
        } catch {
          throw new ApiClientError(0, 'NETWORK', 'Không gửi được tệp lên kho lưu trữ');
        }
        if (!response.ok) {
          throw new ApiClientError(response.status, 'UPLOAD_INCOMPLETE', 'Tệp chưa được tải lên đầy đủ');
        }
      }

      patch(localId, { status: 'verifying' });
      const session = await api<UploadDTO>(`/api/uploads/${created.session.id}/finalize`, {
        method: 'POST', body: {},
      });
      patch(localId, session.state === 'verified'
        ? { status: 'verified', uploadId: session.id }
        : { status: 'failed', error: 'Máy chủ chưa xác nhận được tệp này. Hãy tải lại tệp.' });
    } catch (error) {
      patch(localId, { status: 'failed', error: uploadFailureMessage(error) });
    }
  }, [patch]);

  const add = useCallback((chosen: readonly File[]) => {
    const entries = chosen.map((file) => {
      nextLocalId.current += 1;
      return { localId: `f${nextLocalId.current}`, status: 'queued' as const, file };
    });
    setFiles((list) => [...list, ...entries]);
    for (const entry of entries) void run(entry.localId, entry.file);
    return entries.map((entry) => entry.localId);
  }, [run]);

  const remove = useCallback((localId: string) => {
    setFiles((list) => list.filter((entry) => entry.localId !== localId));
  }, []);

  const retry = useCallback((localId: string) => {
    setFiles((list) => {
      const entry = list.find((candidate) => candidate.localId === localId);
      if (entry) void run(localId, entry.file);
      return list;
    });
  }, [run]);

  const verifiedIds = useMemo(
    () => files.filter((entry) => entry.status === 'verified' && entry.uploadId).map((entry) => entry.uploadId!),
    [files],
  );

  return {
    files, add, remove, retry, verifiedIds,
    busy: files.some((entry) => entry.status === 'queued' || entry.status === 'uploading' || entry.status === 'verifying'),
    blocked: files.some((entry) => entry.status === 'failed'),
    clear: useCallback(() => setFiles([]), []),
    drop: useCallback((uploadIds: readonly Id[]) => {
      setFiles((list) => list.filter((entry) => entry.uploadId === undefined || !uploadIds.includes(entry.uploadId)));
    }, []),
  };
}

export function FilePicker({ queue, label = 'Tệp đính kèm', disabled }: {
  queue: FileQueue;
  label?: string;
  disabled?: boolean;
}) {
  const hintId = useId();
  const [dragging, setDragging] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div
        role="group" aria-label={label} className={DROP_ZONE}
        data-dragging={dragging ? '' : undefined} aria-disabled={disabled ? true : undefined}
        onDragOver={(event) => {
          if (disabled || !event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(event) => {
          setDragging(false);
          if (disabled) return;
          event.preventDefault();
          const chosen = Array.from(event.dataTransfer.files);
          if (chosen.length > 0) queue.add(chosen);
        }}
      >
        {/* The browser's file control stays underneath, out of sight; its label is the button people press. */}
        <label className={ATTACH_BUTTON} data-disabled={disabled ? '' : undefined}>
          <input
            type="file" multiple disabled={disabled} className="sr-only" aria-describedby={hintId}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              if (chosen.length > 0) queue.add(chosen);
              // The same file must be choosable again after it was removed or failed.
              event.target.value = '';
            }}
          />
          <Paperclip aria-hidden="true" className="size-4" />
          Đính kèm tệp
        </label>
        <span aria-hidden="true" className="text-sm text-[hsl(var(--h-muted-foreground))]">Kéo tệp vào đây</span>
      </div>
      <p id={hintId} className={cn(FIELD_HINT, '-mt-2')}>Tệp được gửi cùng bình luận.</p>

      <PickedFiles queue={queue} />
    </div>
  );
}

/** The files chosen so far, each with its state and its way out; "Việc mới" shows it under its pills. */
export function PickedFiles({ queue, className }: { queue: FileQueue; className?: string }) {
  if (queue.files.length === 0) return null;
  return (
    <ul className={cn('flex flex-col divide-y divide-[hsl(var(--h-border))] rounded-md border border-[hsl(var(--h-border))]', className)}>
      {queue.files.map((entry) => (
        <li key={entry.localId} className="flex items-start gap-3 py-1.5 pl-3 pr-1.5">
          <Paperclip className="mt-3 size-4 shrink-0 text-[hsl(var(--h-muted-foreground))]" aria-hidden="true" />
          {/* The name keeps a readable width: on a phone the badge and the buttons drop under it rather
              than squeezing it into a column one syllable wide. */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-[12rem] flex-1 py-2 text-sm font-medium text-[hsl(var(--h-card-foreground))] wrap-anywhere">
              {entry.file.name}
            </span>
            <Badge variant={STATUS_VARIANT[entry.status]}>{STATUS_LABEL[entry.status]}</Badge>
            {entry.error && (
              <span role="status" className={cn(FIELD_ERROR, 'basis-full')}>{entry.error}</span>
            )}
            {/* The file's name stays in each button's accessible name, so a list of several files is never a
                column of identical "Remove" buttons to a screen reader; the eye already has it on the row. */}
            <span className="ml-auto flex shrink-0 items-center">
              {entry.status === 'failed' && (
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => queue.retry(entry.localId)}
                >
                  <RotateCcw aria-hidden="true" />
                  Thử lại tệp<span className="sr-only">{` ${entry.file.name}`}</span>
                </Button>
              )}
              <Button
                type="button" variant="ghost" size="sm"
                className="text-[hsl(var(--h-muted-foreground))] hover:bg-[hsl(var(--h-error-soft))] hover:text-[hsl(var(--h-error-fg))]"
                onClick={() => queue.remove(entry.localId)}
              >
                <X aria-hidden="true" />
                Bỏ tệp<span className="sr-only">{` ${entry.file.name}`}</span>
              </Button>
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
